// ActionMenu — the menu of resolved rows every door wears (ADR-036 §7): the
// entity's Actions door — the page's and the peek's, drawn by the shells from
// the registry (`doorOf(acts, host)`) — a list's ⋯ (ADR-048), a note's ⋯, the
// bulk bar later. The rows arrive from ONE source per entity (`toMenuRows` /
// `doorOf` over the resolved acts; a hand-built `ResolvedAction[]` for a menu
// that is not an entity's), so two doors onto one set can't drift (dev's
// per-page-reinvented-menu failure mode). Props-only rung-2 composite
// (COMPONENTS §Component taxonomy).
//
// Presentational by contract (like EntityHeader's resolved tabs): it takes
// ALREADY-resolved + ALREADY-ordered items — visibility (gate × entitlement ×
// lifecycle-status) and label/route resolution happen UPSTREAM in the consumer,
// so this stays i18n- and permission-agnostic. Renders nothing when the set is
// empty.
//
// Three trigger faces:
//   • 'responsive' (default) — a `···` IconButton below sm, a labelled "Actions ▾"
//     Button on sm+ (the entity-header door; the pill→icon collapse the chrome uses).
//   • 'labelled' — ALWAYS the labelled "Actions ▾" Button, every width (the peek
//     footer door — architect-ruled at P: in the peek the door never shrinks to a
//     bare icon; the footer row has the room, and on touch a labelled door is the
//     discoverable one).
//   • 'icon' — always a `···` IconButton (a list row's cell; the bulk bar).
// A separator fences each `group` change — for an entity's acts the fence derives
// from the kind (edit → operation → lifecycle → danger, `fenceOf`) — so a
// destructive item sits behind a visual gap.

import { Fragment } from 'react';
import { icons } from '../ui/icons.js';
import { Menu, MenuItem, MenuSeparator, type MenuPlacement } from '../ui/Menu.js';
import { Button } from '../ui/Button.js';
import { IconButton } from '../ui/IconButton.js';
import type { ResolvedAction } from './entityRegistry.js';

// The row shape lives beside the registry (`entityRegistry.ts` — `toMenuRows` /
// `doorOf` derive it from an entity's acts); re-exported here for the menus that
// build their rows by hand (a list's ⋯, a note's ⋯).
export type { ResolvedAction };

export interface ActionMenuProps {
  /** Accessible name for the menu + the labelled (sm+) trigger text. */
  label: string;
  /** Resolved, ordered rows (`doorOf` / `toMenuRows` over an entity's acts, or a hand-built list). */
  items: ResolvedAction[];
  /** `responsive` (default) = icon below sm / labelled button sm+; `labelled` = the
   *  labelled button at every width (the peek footer door); `icon` = always `···`. */
  trigger?: 'responsive' | 'labelled' | 'icon';
  /** Trigger button variant. Default `secondary` (the header); a row uses `ghost`. */
  variant?: 'primary' | 'secondary' | 'ghost';
  /** Panel placement relative to the trigger (logical — flips under RTL). Default `bottom end`. */
  placement?: MenuPlacement;
  /** Responsive collapse point for the labelled→icon trigger (`responsive` mode only).
   *  `sm` (default — entity surfaces) · `md` (the list-header door, so the ⋯ snaps to a
   *  square WITH the primary CreateButton + hamburger at one width — ADR-048). */
  collapseAt?: 'sm' | 'md';
}

export function ActionMenu({
  label,
  items,
  trigger = 'responsive',
  variant = 'secondary',
  placement = 'bottom end',
  collapseAt = 'sm',
}: ActionMenuProps) {
  // Empty set → no door at all (a permission-less user sees nothing, not an empty menu).
  if (items.length === 0) return null;

  // The shared menu body — items in order, a separator fencing each group change.
  const body = items.map((item, i) => {
    const prev = items[i - 1];
    const fence = i > 0 && prev?.group !== item.group;
    const href = 'route' in item.run ? item.run.route : undefined;
    const onAction = 'handler' in item.run ? item.run.handler : undefined;
    return (
      <Fragment key={item.id}>
        {fence && <MenuSeparator />}
        <MenuItem
          href={href}
          onAction={onAction}
          icon={item.icon}
          tone={item.tone}
          isDisabled={item.disabled}
          description={item.disabled ? item.note : undefined}
          trailing={item.trailing}
        >
          {item.label}
        </MenuItem>
      </Fragment>
    );
  });

  const iconTrigger = () => (
    <IconButton variant={variant} aria-label={label}>
      <icons.more size={18} aria-hidden />
    </IconButton>
  );

  const labelledTrigger = () => (
    <Button variant={variant === 'ghost' ? 'secondary' : variant}>
      {label}
      <icons.caretDown size={14} aria-hidden />
    </Button>
  );

  if (trigger === 'icon') {
    return (
      <Menu label={label} placement={placement} renderTrigger={iconTrigger}>
        {body}
      </Menu>
    );
  }

  if (trigger === 'labelled') {
    return (
      <Menu label={label} placement={placement} renderTrigger={labelledTrigger}>
        {body}
      </Menu>
    );
  }

  // Responsive: the `···` icon below the collapse point, the labelled "Actions ▾"
  // button above it (two Menu instances behind a show/hide, so each uses its
  // proper primitive — the established chrome pattern; one is display:none at any
  // width). `collapseAt` picks the breakpoint: sm (default) or md (list header).
  const iconOnly = collapseAt === 'md' ? 'md:hidden' : 'sm:hidden';
  const labelled = collapseAt === 'md' ? 'hidden md:inline-flex' : 'hidden sm:inline-flex';
  return (
    <>
      <span className={iconOnly}>
        <Menu label={label} placement={placement} renderTrigger={iconTrigger}>
          {body}
        </Menu>
      </span>
      <span className={labelled}>
        <Menu label={label} placement={placement} renderTrigger={labelledTrigger}>
          {body}
        </Menu>
      </span>
    </>
  );
}
